import { createContext, useContext, useRef } from "react"
import { useTransform } from "framer-motion"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"

import useConstant from "hooks/useConstant"

export const Context = createContext()

const XTRAF = 60
const PPF = 18

export const ErsiveProvider = ({ first_frame, start, end, text_stop, children }) => {
  const console = useConsole()
  const viewport = useViewport()

  const canCtx = useRef()

  const sources = useConstant(
    () =>
      new Map([
        ["portrait", first_frame.sources[0]],
        ["landscape", first_frame.sources[1]],
      ])
  )

  const frames = useConstant(
    () =>
      new Map([
        ["portrait", new Map()],
        ["landscape", new Map()],
      ])
  )

  const frameNb = useConstant(() => end - start)
  const extraFrames = useConstant(() => text_stop.reduce((a, t) => (t.end - t.start === 0 ? a + XTRAF : a), 0))

  const rootHeight = useTransform(viewport.height, h => Math.max(h * 1.5, (frameNb + extraFrames) * PPF))

  const output = useConstant(() =>
    text_stop
      .reduce((a, t) => a.concat([(t.start - 1) / frameNb, (t.end - 1) / frameNb]), [])
      .concat([0, 1])
      .sort((a, b) => a - b)
  )
  const input = useConstant(() => {
    let extraF = 0
    return text_stop
      .reduce((a, t) => {
        const range = t.start === t.end ? [t.start + extraF, t.end + XTRAF + extraF] : [t.start + extraF, t.end + extraF]
        extraF += t.start === t.end ? XTRAF : 0
        return a.concat(range)
      }, [])
      .reduce((a, t) => a.concat(t - 1), [])
      .concat([0, frameNb + extraFrames])
      .sort((a, b) => a - b)
      .reduce((a, t) => a.concat([t / (frameNb + extraFrames)]), [])
  })

  const inTxt = useConstant(() => {
    let extraF = 0
    return text_stop.reduce((a, t) => {
      const fNb = t.end === t.start ? XTRAF : t.end - t.start
      const inp = [t.start + extraF, t.start + extraF + fNb * 0.25, t.start + extraF + fNb * 0.75, t.start + extraF + fNb].reduce(
        (a, t) => a.concat([Math.max(t - 1, 0) / (frameNb + extraFrames)]),
        []
      )
      extraF += t.start === t.end ? XTRAF : 0
      return a.concat([inp])
    }, [])
  })

  return <Context.Provider value={{ sources, frames, frameNb, canCtx, rootHeight, input, output, inTxt }}>{children}</Context.Provider>
}

export const useErsive = () => useContext(Context)
